import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { EMPTY, Observable } from 'rxjs';

import { redactUrl } from '../../../datadog-redaction';
import { TutorialNetworkGuardService } from 'app/shared/services/tutorial-network-guard.service';

/**
 * Requests a simulated tour is allowed to make. Everything else under `api/` is refused while one is running.
 *
 * Deliberately a short, explicit allowlist rather than a blocklist: a blocklist silently gains a hole every time
 * someone adds an endpoint, which is the failure this interceptor exists to prevent.
 */
const TUTORIAL_ALLOWED_PREFIXES: readonly string[] = [
    // Whether this user has been offered a tour, and recording the answer.
    'api/WorkflowTutorial/'

    // Authentication and session. Not operational data, and blocking these would be actively harmful: the
    // inactivity timeout signs out through `SingleSignOut`/`Logout`, and swallowing that would leave a timed-out
    // session alive behind the overlay. `GetUserInfo` is how the app refreshes its security context after a 403.
    , 'api/Account/'

    // Background reads the real-time notification service makes when the server pushes an event. Read-only, and
    // not something the user did inside the tour.
    , 'api/online/'
    , 'api/Home/GetOptions'
];

/**
 * Refuses operational HTTP while a simulated guided tour is running. See {@link TutorialNetworkGuardService}
 * for why this exists alongside the per-handler `tutorialMode` guards.
 *
 * A refused request is never handed to the next handler, and the caller's observable completes without emitting.
 * Not an error: an error would run the caller's error branch — a toast or a navigation in the middle of the tour.
 * That is not the same as swallowing it: every block is logged as an error to the console and to Datadog,
 * naming the method and URL, because a block means an operational path ran that its tutorial guard should have
 * stopped, and that is a defect that needs to be findable.
 *
 * Registered FIRST, ahead of `HttpClientInterceptor`, so a refused request never touches the spinner count.
 */
@Injectable()
export class TutorialHttpGuardInterceptor implements HttpInterceptor {
    private readonly guard = inject(TutorialNetworkGuardService);

    intercept(
        req: HttpRequest<unknown>
        , next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        if (!this.guard.armed()) { return next.handle(req); }
        if (!isApiRequest(req.url)) { return next.handle(req); }
        if (isAllowedDuringTutorial(req.url)) { return next.handle(req); }

        // Path only. The message goes to Datadog, and its redaction strips query strings from absolute URLs
        // only — a relative `api/...?id=` would otherwise arrive with its parameters intact.
        const message = `Blocked ${req.method} ${redactUrl(req.url)} during a guided tour.`
            + ' An operational handler ran that should have been guarded.';
        console.error(message);
        datadogLogs.logger.error(message);

        return EMPTY;
    }
}

/**
 * Only HOMER's own API is policed. Assets, templates, and third-party URLs are not operational writes, and
 * blocking them would break the tour's own rendering.
 */
function isApiRequest(url: string): boolean {
    return url.startsWith('api/')
        || url.startsWith('/api/');
}

/** Whether `url` is on the allowlist, tolerating the optional leading slash. Case-insensitive, like the routes. */
function isAllowedDuringTutorial(url: string): boolean {
    const normalized = (url.startsWith('/') ? url.slice(1) : url).toLowerCase();
    return TUTORIAL_ALLOWED_PREFIXES.some(prefix => normalized.startsWith(prefix.toLowerCase()));
}
